import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Calendar, Plus, Pencil, Trash2, Repeat, Clock, Car, Receipt, TrendingUp, TrendingDown, CalendarClock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const expenseCategories = [
  { value: 'fuel', label: 'Fuel' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'toll', label: 'Toll Fees' },
  { value: 'salary', label: 'Salaries' },
  { value: 'office', label: 'Office' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'other', label: 'Other' },
];

const incomeCategories = [
  { value: 'transport', label: 'Transport Services' },
  { value: 'owner_driver', label: 'Owner Driver Revenue' },
  { value: 'rental', label: 'Vehicle Rental' },
  { value: 'contract', label: 'Contract Income' },
  { value: 'commission', label: 'Commission' },
  { value: 'other_income', label: 'Other Income' },
];

const vatTypes = [
  { value: 'inclusive', label: 'VAT Inclusive (15%)' },
  { value: 'exclusive', label: 'VAT Exclusive' },
  { value: 'no_vat', label: 'No VAT' },
];

export default function Forecast() {
  const [forecasts, setForecasts] = useState([]);
  const [actualData, setActualData] = useState(null);
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingForecast, setEditingForecast] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [form, setForm] = useState({
    description: '',
    amount: '',
    forecast_type: 'recurring',
    entry_type: 'expense', // NEW: 'expense' or 'income'
    category: 'other',
    vat_type: 'inclusive',
    vehicle_registration: '',
    start_month: new Date().toISOString().slice(0, 7),
    end_month: '',
    notes: ''
  });

  useEffect(() => {
    fetchFinancialYears();
    fetchForecasts();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      fetchActualData();
    }
  }, [selectedYear]);

  const fetchFinancialYears = async () => {
    try {
      const response = await axios.get(`${API_URL}/finance/years`);
      const years = response.data.financial_years || response.data.years || response.data || [];
      setFinancialYears(Array.isArray(years) ? years : []);
      if (years.length > 0) {
        setSelectedYear(years[0]);
      }
    } catch (error) {
      console.error('Failed to load financial years:', error);
    }
  };

  const fetchActualData = async () => {
    try {
      // Use the new endpoint that includes manual input VAT
      const response = await axios.get(`${API_URL}/finance/actual-by-vat-period-with-manual?financial_year=${selectedYear}`);
      setActualData(response.data);
    } catch (error) {
      console.error('Failed to load actual data:', error);
    }
  };

  const handleManualVATChange = async (period, amount) => {
    try {
      await axios.post(`${API_URL}/finance/manual-input-vat`, {
        period: period,
        financial_year: selectedYear,
        amount: parseFloat(amount) || 0,
        description: 'Manual purchases VAT'
      });
      toast.success(`Input VAT updated for ${period}`);
      fetchActualData(); // Refresh data
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update Input VAT'));
    }
  };

  const fetchForecasts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/finance/forecast`);
      setForecasts(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load forecasts'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        end_month: form.end_month || null
      };

      if (editingForecast) {
        await axios.put(`${API_URL}/finance/forecast/${editingForecast.id}`, payload);
        toast.success('Forecast updated');
      } else {
        await axios.post(`${API_URL}/finance/forecast`, payload);
        toast.success('Forecast created');
      }

      setShowDialog(false);
      resetForm();
      fetchForecasts();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save forecast'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (forecast) => {
    if (!confirm(`Delete forecast "${forecast.description}"?`)) return;

    try {
      await axios.delete(`${API_URL}/finance/forecast/${forecast.id}`);
      toast.success('Forecast deleted');
      fetchForecasts();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete forecast'));
    }
  };

  const openEditDialog = (forecast) => {
    setEditingForecast(forecast);
    setForm({
      description: forecast.description,
      amount: forecast.amount.toString(),
      forecast_type: forecast.forecast_type,
      entry_type: forecast.entry_type || 'expense',
      category: forecast.category,
      vat_type: forecast.vat_type || 'inclusive',
      vehicle_registration: forecast.vehicle_registration || '',
      start_month: forecast.start_month,
      end_month: forecast.end_month || '',
      notes: forecast.notes || ''
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingForecast(null);
    setForm({
      description: '',
      amount: '',
      forecast_type: 'recurring',
      entry_type: 'expense',
      category: 'other',
      vat_type: 'inclusive',
      vehicle_registration: '',
      start_month: new Date().toISOString().slice(0, 7),
      end_month: '',
      notes: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount || 0);
  };

  const recurringForecasts = forecasts.filter(f => f.forecast_type === 'recurring');
  const onceOffForecasts = forecasts.filter(f => f.forecast_type === 'once_off');
  
  // Separate income and expense forecasts
  const recurringExpenses = recurringForecasts.filter(f => f.entry_type !== 'income');
  const recurringIncome = recurringForecasts.filter(f => f.entry_type === 'income');
  const onceOffExpenses = onceOffForecasts.filter(f => f.entry_type !== 'income');
  const onceOffIncome = onceOffForecasts.filter(f => f.entry_type === 'income');

  // Expense totals
  const totalMonthlyExpenses = recurringExpenses.reduce((sum, f) => sum + (f.net_excl_vat || f.amount || 0), 0);
  const totalOnceOffExpenses = onceOffExpenses.reduce((sum, f) => sum + (f.net_excl_vat || f.amount || 0), 0);
  
  // Income totals
  const totalMonthlyIncome = recurringIncome.reduce((sum, f) => sum + (f.net_excl_vat || f.amount || 0), 0);
  const totalOnceOffIncome = onceOffIncome.reduce((sum, f) => sum + (f.net_excl_vat || f.amount || 0), 0);
  
  // Calculate Input VAT (VAT you can claim back from expenses)
  const totalExpenseVAT = recurringExpenses.reduce((sum, f) => sum + (f.vat_amount || 0), 0);
  const totalOnceOffExpenseVAT = onceOffExpenses.reduce((sum, f) => sum + (f.vat_amount || 0), 0);
  const totalInputVAT = totalExpenseVAT + totalOnceOffExpenseVAT;
  
  // Calculate Output VAT (VAT you need to pay from income)
  const totalIncomeVAT = recurringIncome.reduce((sum, f) => sum + (f.vat_amount || 0), 0);
  const totalOnceOffIncomeVAT = onceOffIncome.reduce((sum, f) => sum + (f.vat_amount || 0), 0);
  const totalOutputVAT = totalIncomeVAT + totalOnceOffIncomeVAT;
  
  // Net VAT (Output - Input = what you owe SARS)
  const netVAT = totalOutputVAT - totalInputVAT;
  
  // Legacy variables for backward compatibility
  const totalMonthlyRecurring = totalMonthlyExpenses;
  const totalOnceOff = totalOnceOffExpenses;
  const totalRecurringVAT = totalExpenseVAT;
  const totalOnceOffVAT = totalOnceOffExpenseVAT;

  // Calculate profit (Income - Expenses, excluding VAT)
  const monthlyProfit = totalMonthlyIncome - totalMonthlyExpenses;
  const totalProfit = (totalMonthlyIncome + totalOnceOffIncome) - (totalMonthlyExpenses + totalOnceOffExpenses);
  
  // Total income and expenses (recurring + once-off)
  const grandTotalIncome = totalMonthlyIncome + totalOnceOffIncome;
  const grandTotalExpenses = totalMonthlyExpenses + totalOnceOffExpenses;
  
  // All income items combined
  const allIncomeForecasts = [...recurringIncome, ...onceOffIncome];
  const allExpenseForecasts = [...recurringExpenses, ...onceOffExpenses];

  // Group forecasts by month
  const groupByMonth = (items) => {
    const grouped = {};
    items.forEach(item => {
      const month = item.start_month || 'Unknown';
      if (!grouped[month]) {
        grouped[month] = [];
      }
      grouped[month].push(item);
    });
    // Sort months
    return Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0]));
  };

  const incomeByMonth = groupByMonth(allIncomeForecasts);
  const expensesByMonth = groupByMonth(allExpenseForecasts);

  // Get month name from YYYY-MM format
  const getMonthName = (monthStr) => {
    if (!monthStr || monthStr === 'Unknown') return monthStr;
    const [year, month] = monthStr.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  // Calculate totals for a month's items
  const calculateMonthTotals = (items) => {
    const inclVat = items.reduce((sum, f) => sum + (f.net_excl_vat || f.amount || 0) + (f.vat_amount || 0), 0);
    const exclVat = items.reduce((sum, f) => sum + (f.net_excl_vat || f.amount || 0), 0);
    const vat = items.reduce((sum, f) => sum + (f.vat_amount || 0), 0);
    return { inclVat, exclVat, vat };
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="forecast-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Forecast</h1>
            <p className="text-muted-foreground mt-1">Plan recurring and once-off income & expenses</p>
          </div>
          <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-forecast-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Forecast
          </Button>
        </div>

        {/* Summary - Row 1: Income, Expenses, Profit (ALL items) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-8 w-8 text-green-600" />
                <div>
                  <p className="text-sm text-green-700">Total Income (Excl VAT)</p>
                  <p className="text-2xl font-bold text-green-700">{formatCurrency(grandTotalIncome)}</p>
                  <p className="text-xs text-green-600">
                    {recurringIncome.length} recurring | {onceOffIncome.length} once-off
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <TrendingDown className="h-8 w-8 text-red-600" />
                <div>
                  <p className="text-sm text-red-700">Total Expenses (Excl VAT)</p>
                  <p className="text-2xl font-bold text-red-700">{formatCurrency(grandTotalExpenses)}</p>
                  <p className="text-xs text-red-600">
                    {recurringExpenses.length} recurring | {onceOffExpenses.length} once-off
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={totalProfit >= 0 ? "border-emerald-300 bg-emerald-100" : "border-orange-300 bg-orange-100"}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Receipt className={`h-8 w-8 ${totalProfit >= 0 ? 'text-emerald-600' : 'text-orange-600'}`} />
                <div>
                  <p className={`text-sm ${totalProfit >= 0 ? 'text-emerald-700' : 'text-orange-700'}`}>
                    Total Profit / Loss
                  </p>
                  <p className={`text-2xl font-bold ${totalProfit >= 0 ? 'text-emerald-700' : 'text-orange-700'}`}>
                    {totalProfit >= 0 ? '' : '-'}{formatCurrency(Math.abs(totalProfit))}
                  </p>
                  <p className={`text-xs ${totalProfit >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                    {totalProfit >= 0 ? 'Money Left Over' : 'Shortfall'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Summary - Row 2: VAT Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-purple-200 bg-purple-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <Receipt className="h-6 w-6 text-purple-600" />
                <div>
                  <p className="text-sm text-purple-700">Output VAT (Payable)</p>
                  <p className="text-xl font-bold text-purple-700">{formatCurrency(totalOutputVAT)}</p>
                  <p className="text-xs text-purple-600">From income</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <Receipt className="h-6 w-6 text-blue-600" />
                <div>
                  <p className="text-sm text-blue-700">Input VAT (Claimable)</p>
                  <p className="text-xl font-bold text-blue-700">{formatCurrency(totalInputVAT)}</p>
                  <p className="text-xs text-blue-600">From expenses</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={netVAT >= 0 ? "border-orange-200 bg-orange-50" : "border-teal-200 bg-teal-50"}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <Receipt className={`h-6 w-6 ${netVAT >= 0 ? 'text-orange-600' : 'text-teal-600'}`} />
                <div>
                  <p className={`text-sm ${netVAT >= 0 ? 'text-orange-700' : 'text-teal-700'}`}>Net VAT</p>
                  <p className={`text-xl font-bold ${netVAT >= 0 ? 'text-orange-700' : 'text-teal-700'}`}>
                    {formatCurrency(Math.abs(netVAT))}
                  </p>
                  <p className={`text-xs ${netVAT >= 0 ? 'text-orange-600' : 'text-teal-600'}`}>
                    {netVAT >= 0 ? 'Payable to SARS' : 'Refund from SARS'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actual Data by Bi-Monthly VAT Period */}
        <Card className="border-2 border-blue-200">
          <CardHeader className="bg-blue-50">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CalendarClock className="h-5 w-5 text-blue-600" />
                  Actual Data by VAT Period
                </CardTitle>
                <CardDescription>Actual income & expenses grouped by bi-monthly VAT cycles (editable manual VAT)</CardDescription>
              </div>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select Year" />
                </SelectTrigger>
                <SelectContent>
                  {financialYears.map((fy) => (
                    <SelectItem key={fy} value={fy}>
                      {fy}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {actualData ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>VAT Period</TableHead>
                    <TableHead className="text-right text-green-700">Income</TableHead>
                    <TableHead className="text-right text-red-700">Expenses</TableHead>
                    <TableHead className="text-right text-purple-700">Output VAT</TableHead>
                    <TableHead className="text-right text-blue-700">Auto Input VAT</TableHead>
                    <TableHead className="text-right text-cyan-700">Manual Input VAT</TableHead>
                    <TableHead className="text-right text-indigo-700 font-semibold">Total Input VAT</TableHead>
                    <TableHead className="text-right text-orange-700">Net VAT</TableHead>
                    <TableHead className="text-right">Profit/Loss</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {actualData.periods?.map((period, idx) => (
                    <TableRow key={idx} className={period.profit >= 0 ? '' : 'bg-red-50'}>
                      <TableCell className="font-medium">{period.period}</TableCell>
                      <TableCell className="text-right text-green-700">{formatCurrency(period.income)}</TableCell>
                      <TableCell className="text-right text-red-700">{formatCurrency(period.expenses)}</TableCell>
                      <TableCell className="text-right text-purple-700">{formatCurrency(period.output_vat)}</TableCell>
                      <TableCell className="text-right text-blue-700">{formatCurrency(period.input_vat)}</TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          step="0.01"
                          className="w-28 h-8 text-right text-cyan-700 border-cyan-300 focus:border-cyan-500"
                          placeholder="0.00"
                          defaultValue={period.manual_input_vat || ''}
                          onBlur={(e) => {
                            const newValue = parseFloat(e.target.value) || 0;
                            if (newValue !== (period.manual_input_vat || 0)) {
                              handleManualVATChange(period.period, e.target.value);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.target.blur();
                            }
                          }}
                          data-testid={`manual-vat-${period.period}`}
                        />
                      </TableCell>
                      <TableCell className="text-right text-indigo-700 font-semibold">{formatCurrency(period.total_input_vat || period.input_vat)}</TableCell>
                      <TableCell className={`text-right font-medium ${period.net_vat >= 0 ? 'text-orange-700' : 'text-teal-700'}`}>
                        {period.net_vat >= 0 ? formatCurrency(period.net_vat) : `(${formatCurrency(Math.abs(period.net_vat))})`}
                      </TableCell>
                      <TableCell className={`text-right font-bold ${period.profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {period.profit >= 0 ? '' : '-'}{formatCurrency(Math.abs(period.profit))}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Totals Row */}
                  <TableRow className="bg-blue-100 font-bold border-t-2">
                    <TableCell>TOTAL ({selectedYear})</TableCell>
                    <TableCell className="text-right text-green-700">{formatCurrency(actualData.totals?.income || 0)}</TableCell>
                    <TableCell className="text-right text-red-700">{formatCurrency(actualData.totals?.expenses || 0)}</TableCell>
                    <TableCell className="text-right text-purple-700">{formatCurrency(actualData.totals?.output_vat || 0)}</TableCell>
                    <TableCell className="text-right text-blue-700">{formatCurrency(actualData.totals?.input_vat || 0)}</TableCell>
                    <TableCell className="text-right text-cyan-700">{formatCurrency(actualData.totals?.manual_input_vat || 0)}</TableCell>
                    <TableCell className="text-right text-indigo-700">{formatCurrency(actualData.totals?.total_input_vat || actualData.totals?.input_vat || 0)}</TableCell>
                    <TableCell className={`text-right font-medium ${(actualData.totals?.net_vat || 0) >= 0 ? 'text-orange-700' : 'text-teal-700'}`}>
                      {(actualData.totals?.net_vat || 0) >= 0 
                        ? formatCurrency(actualData.totals?.net_vat || 0) 
                        : `(${formatCurrency(Math.abs(actualData.totals?.net_vat || 0))})`}
                    </TableCell>
                    <TableCell className={`text-right font-bold ${(actualData.totals?.profit || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {(actualData.totals?.profit || 0) >= 0 ? '' : '-'}{formatCurrency(Math.abs(actualData.totals?.profit || 0))}
                    </TableCell>
                  </TableRow>
                  {/* Combined Actual + Forecast Row */}
                  <TableRow className="bg-purple-100 font-bold border-t-2">
                    <TableCell>ACTUAL + FORECAST</TableCell>
                    <TableCell className="text-right text-green-700">
                      {formatCurrency((actualData.totals?.income || 0) + grandTotalIncome)}
                    </TableCell>
                    <TableCell className="text-right text-red-700">
                      {formatCurrency((actualData.totals?.expenses || 0) + grandTotalExpenses)}
                    </TableCell>
                    <TableCell className="text-right text-purple-700">
                      {formatCurrency((actualData.totals?.output_vat || 0) + totalOutputVAT)}
                    </TableCell>
                    <TableCell className="text-right text-blue-700">
                      {formatCurrency((actualData.totals?.input_vat || 0) + totalInputVAT)}
                    </TableCell>
                    <TableCell className="text-right text-cyan-700">
                      {formatCurrency(actualData.totals?.manual_input_vat || 0)}
                    </TableCell>
                    <TableCell className="text-right text-indigo-700">
                      {formatCurrency((actualData.totals?.total_input_vat || actualData.totals?.input_vat || 0) + totalInputVAT)}
                    </TableCell>
                    <TableCell className={`text-right font-medium ${((actualData.totals?.net_vat || 0) + netVAT) >= 0 ? 'text-orange-700' : 'text-teal-700'}`}>
                      {((actualData.totals?.net_vat || 0) + netVAT) >= 0 
                        ? formatCurrency((actualData.totals?.net_vat || 0) + netVAT) 
                        : `(${formatCurrency(Math.abs((actualData.totals?.net_vat || 0) + netVAT))})`}
                    </TableCell>
                    <TableCell className={`text-right font-bold ${((actualData.totals?.profit || 0) + totalProfit) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {((actualData.totals?.profit || 0) + totalProfit) >= 0 ? '' : '-'}
                      {formatCurrency(Math.abs((actualData.totals?.profit || 0) + totalProfit))}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Loading actual data...
              </div>
            )}
          </CardContent>
        </Card>

        {/* Forecasted Income by Month */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Forecasted Income by Month
            </CardTitle>
            <CardDescription>Income forecasts grouped by month</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {incomeByMonth.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No income forecasts set up. Click "Add Forecast" to add income.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Incl VAT</TableHead>
                    <TableHead className="text-right">Excl VAT</TableHead>
                    <TableHead className="text-right text-purple-700">Output VAT</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {incomeByMonth.map(([month, items]) => {
                    const monthTotals = calculateMonthTotals(items);
                    return (
                      <React.Fragment key={month}>
                        {/* Month Header */}
                        <TableRow className="bg-green-50 border-t-2">
                          <TableCell colSpan={7} className="font-bold text-green-800">
                            {getMonthName(month)}
                          </TableCell>
                        </TableRow>
                        {/* Items for this month */}
                        {items.map((forecast) => {
                          const exclVat = forecast.net_excl_vat || forecast.amount || 0;
                          const vatAmount = forecast.vat_amount || 0;
                          const inclVat = exclVat + vatAmount;
                          return (
                            <TableRow key={forecast.id}>
                              <TableCell></TableCell>
                              <TableCell className="font-medium">{forecast.description}</TableCell>
                              <TableCell>
                                <Badge variant={forecast.forecast_type === 'recurring' ? 'default' : 'secondary'} className="text-xs">
                                  {forecast.forecast_type === 'recurring' ? 'Monthly' : 'Once-Off'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">{formatCurrency(inclVat)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(exclVat)}</TableCell>
                              <TableCell className="text-right text-purple-700">{vatAmount > 0 ? formatCurrency(vatAmount) : '-'}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button variant="ghost" size="sm" onClick={() => openEditDialog(forecast)}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleDelete(forecast)} className="text-red-600">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {/* Month Subtotal */}
                        <TableRow className="bg-green-100/50">
                          <TableCell colSpan={3} className="font-semibold text-right">{getMonthName(month)} Subtotal:</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(monthTotals.inclVat)}</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(monthTotals.exclVat)}</TableCell>
                          <TableCell className="text-right font-semibold text-purple-700">{formatCurrency(monthTotals.vat)}</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                  {/* Grand Total */}
                  <TableRow className="bg-green-200 font-bold border-t-2">
                    <TableCell colSpan={3} className="text-right">TOTAL INCOME</TableCell>
                    <TableCell className="text-right">{formatCurrency(grandTotalIncome + totalOutputVAT)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(grandTotalIncome)}</TableCell>
                    <TableCell className="text-right text-purple-700">{formatCurrency(totalOutputVAT)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Forecasted Expenses by Month */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-red-600" />
              Forecasted Expenses by Month
            </CardTitle>
            <CardDescription>Expense forecasts grouped by month</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {expensesByMonth.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No expense forecasts set up. Click "Add Forecast" to add expenses.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Incl VAT</TableHead>
                    <TableHead className="text-right">Excl VAT</TableHead>
                    <TableHead className="text-right text-blue-700">Input VAT</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expensesByMonth.map(([month, items]) => {
                    const monthTotals = calculateMonthTotals(items);
                    return (
                      <React.Fragment key={month}>
                        {/* Month Header */}
                        <TableRow className="bg-red-50 border-t-2">
                          <TableCell colSpan={7} className="font-bold text-red-800">
                            {getMonthName(month)}
                          </TableCell>
                        </TableRow>
                        {/* Items for this month */}
                        {items.map((forecast) => {
                          const exclVat = forecast.net_excl_vat || forecast.amount || 0;
                          const vatAmount = forecast.vat_amount || 0;
                          const inclVat = exclVat + vatAmount;
                          return (
                            <TableRow key={forecast.id}>
                              <TableCell></TableCell>
                              <TableCell className="font-medium">{forecast.description}</TableCell>
                              <TableCell>
                                <Badge variant={forecast.forecast_type === 'recurring' ? 'default' : 'secondary'} className="text-xs">
                                  {forecast.forecast_type === 'recurring' ? 'Monthly' : 'Once-Off'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">{formatCurrency(inclVat)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(exclVat)}</TableCell>
                              <TableCell className="text-right text-blue-700">{vatAmount > 0 ? formatCurrency(vatAmount) : '-'}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button variant="ghost" size="sm" onClick={() => openEditDialog(forecast)}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleDelete(forecast)} className="text-red-600">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {/* Month Subtotal */}
                        <TableRow className="bg-red-100/50">
                          <TableCell colSpan={3} className="font-semibold text-right">{getMonthName(month)} Subtotal:</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(monthTotals.inclVat)}</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(monthTotals.exclVat)}</TableCell>
                          <TableCell className="text-right font-semibold text-blue-700">{formatCurrency(monthTotals.vat)}</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                  {/* Grand Total */}
                  <TableRow className="bg-red-200 font-bold border-t-2">
                    <TableCell colSpan={3} className="text-right">TOTAL EXPENSES</TableCell>
                    <TableCell className="text-right">{formatCurrency(grandTotalExpenses + totalInputVAT)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(grandTotalExpenses)}</TableCell>
                    <TableCell className="text-right text-blue-700">{formatCurrency(totalInputVAT)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingForecast ? 'Edit Forecast' : 'Add Forecast'}</DialogTitle>
              <DialogDescription>
                Set up recurring or planned income & expenses
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type *</Label>
                    <Select value={form.entry_type} onValueChange={(value) => setForm({ ...form, entry_type: value, category: value === 'income' ? 'transport' : 'other' })}>
                      <SelectTrigger className={form.entry_type === 'income' ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="income">
                          <span className="flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-green-600" />
                            Income
                          </span>
                        </SelectItem>
                        <SelectItem value="expense">
                          <span className="flex items-center gap-2">
                            <TrendingDown className="h-4 w-4 text-red-600" />
                            Expense
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Frequency *</Label>
                    <Select value={form.forecast_type} onValueChange={(value) => setForm({ ...form, forecast_type: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="recurring">Recurring Monthly</SelectItem>
                        <SelectItem value="once_off">Once-Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Input
                    id="description"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder={form.entry_type === 'income' ? "e.g., Transport Contract - ABC Ltd" : "e.g., Vehicle Insurance - Truck 1"}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="amount">Amount (R) *</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>VAT Type *</Label>
                    <Select value={form.vat_type} onValueChange={(value) => setForm({ ...form, vat_type: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {vatTypes.map(vat => (
                          <SelectItem key={vat.value} value={vat.value}>{vat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select value={form.category} onValueChange={(value) => setForm({ ...form, category: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(form.entry_type === 'income' ? incomeCategories : expenseCategories).map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vehicle">Vehicle Registration</Label>
                    <Input
                      id="vehicle"
                      value={form.vehicle_registration}
                      onChange={(e) => setForm({ ...form, vehicle_registration: e.target.value })}
                      placeholder="e.g., CA 123-456"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_month">
                      {form.forecast_type === 'recurring' ? 'Start Month *' : 'Expected Month *'}
                    </Label>
                    <Input
                      id="start_month"
                      type="month"
                      value={form.start_month}
                      onChange={(e) => setForm({ ...form, start_month: e.target.value })}
                      required
                    />
                  </div>
                  {form.forecast_type === 'recurring' && (
                    <div className="space-y-2">
                      <Label htmlFor="end_month">End Month (optional)</Label>
                      <Input
                        id="end_month"
                        type="month"
                        value={form.end_month}
                        onChange={(e) => setForm({ ...form, end_month: e.target.value })}
                        placeholder="Leave empty for ongoing"
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={2}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button type="submit" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (editingForecast ? 'Update' : 'Create Forecast')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
